<template>
  <h1 class="product-title">
    <span v-if="brandDisplayName" :class="brandNameStyles" itemprop="brand" itemtype="https://schema.org/Brand" itemscope>
      <a class="link" :href="brandUrl" itemprop="url" @click="tagProductTitleClick" v-html="brandDisplayName"></a>
      <meta itemprop="name" content="{{ brandDisplayName }}" />
    </span>
    <span v-if="titleText" class="product-name" :class="productNameStyles" itemprop="name" v-html="titleText"></span>
  </h1>
  <span v-if="showExclusiveText" class="exclusive-text">
    {{ exclusiveText }}
  </span>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useBrand } from '~/composables/useBrand';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import tagProductTitleClick from '~/features/analytics/productTitle';
import { useDeviceType } from '~/composables/utils/useDeviceType';

const { isMcom } = useBrand();
const { isMobile } = useDeviceType();
const { isChanelFlag, isExclusiveBrandFlag } = useProductFlags();

const { isDetails = false } = defineProps<{
  isDetails?: boolean;
}>();

const { productName: consolidatedName, productExclusiveName: consolidatedExclusiveName } = useConsolidatedProductDetails();
const {
  productName: nameRaw,
  productExclusiveName: exclusiveNameRaw,
  brandDisplayName,
  brandUrl: brandUrlRaw,
  secondaryDescription: description,
  pdfEmailDescription,
} = useCommonProductGetters();

const productName = computed(() => consolidatedName.value || nameRaw);
const productExclusiveName = computed(() => consolidatedExclusiveName.value || exclusiveNameRaw);
const brandUrl = brandDisplayName && brandUrlRaw;

const showExclusiveText = isMcom && isExclusiveBrandFlag;

const exclusiveText = "Macy's Exclusive";

const titleText = computed(() => {
  if (isMcom) {
    let titleArr = [];
    if (showExclusiveText && productExclusiveName.value) {
      titleArr = [description, productExclusiveName.value];
    } else {
      titleArr = [description, productName.value];
    }
    return titleArr.filter(Boolean).join('<br>');
  }
  const bcomProductName = isChanelFlag ? productName.value : pdfEmailDescription;
  return [bcomProductName, description].filter(Boolean).join('<br>');
});

const brandNameStyles = computed(() => {
  return isMobile.value && !isDetails ? 'medium' : 'large';
});

const productNameStyles = computed(() => {
  if (isDetails) {
    return 'body margin-top-xxxs';
  }
  return isMobile.value ? 'medium' : 'large';
});
</script>

<style lang="scss" scoped>
@import '~settingSCSS';
@import '@core/csg/src/scss/typography/typography-mixins.scss';

.link:not(:hover):not(:active):not(:focus) {
  text-decoration: none;
}

.body {
  @include body(regular);
}

.product-title {
  display: flex;
  flex-direction: column;
}

.product-name {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.margin-top-xxxs {
  margin-top: rem-calc(4);
}

.exclusive-text {
  @include body(small);
  color: $gray-1-color;
}
</style>
